import { Component, inject, Input, input, OnDestroy, OnInit, output } from '@angular/core';
import { ZiroIcons } from '../models/ziro-icons.enum';
import { ButtonSizes, ButtonStyles } from '../../button/button.component';
import {
  ClusterResult,
  DirectoryNumberResult,
  DistributionListResult,
  ExtensionMobilityResult,
  PhoneResult,
  SiteResult,
  SiteSummary,
  SnrProfileResult,
  VoicemailResult,
} from '../models/generated/ziroModels';
import { CurrentClusterContext } from '../contexts/current-cluster.context';
import { DevicePoolSites, UiEndUserResult } from '../../helpdesk/search/search.component';
import { Router } from '@angular/router';
import { SiteSummaryContext } from '../contexts/site-summary.context';
import { Subscription } from 'rxjs';

interface SearchResultLink {
  link: string;
  tooltip?: string;
  isDisabled?: boolean;
}

interface SearchResultDeleteButton {
  isDeletable: boolean;
  isDisabled?: boolean;
  tooltip?: string;
}

@Component({
  selector: 'ziro-cisco-search-result',
  templateUrl: './cisco-search-result.component.html',
  styleUrls: ['cisco-search-result.component.scss'],
  standalone: false,
})
export class CiscoSearchResultComponent implements OnInit, OnDestroy {
  private currentClusterContext = inject(CurrentClusterContext);
  private _router = inject(Router);
  private siteSummaryContext = inject(SiteSummaryContext);

  ziroIcons = ZiroIcons;
  buttonStyles = ButtonStyles;
  buttonSizes = ButtonSizes;

  readonly icon = input<ZiroIcons>();
  readonly description = input<string>();
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() edit: SearchResultLink;
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() pinReset: SearchResultLink;
  readonly result = input<
    | DirectoryNumberResult
    | DistributionListResult
    | UiEndUserResult
    | PhoneResult
    | PhoneResult
    | SnrProfileResult
    | VoicemailResult
    | ExtensionMobilityResult
  >();
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() delete: SearchResultDeleteButton;
  readonly devicePoolSites = input<DevicePoolSites>();

  readonly deleteClicked = output<
    | DirectoryNumberResult
    | DistributionListResult
    | UiEndUserResult
    | PhoneResult
    | SnrProfileResult
    | VoicemailResult
    | ExtensionMobilityResult
  >();

  private _subscriptions = new Subscription();

  ngOnInit(): void {
    if (this.result().hasOwnProperty('microsoft365UserRef')) {
      return;
    }

    const siteSummarySub = this.siteSummaryContext.state$.subscribe((siteSummary: SiteSummary) => {
      this._init(siteSummary);
    });
    this._subscriptions.add(siteSummarySub);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  onDeleteClicked(): void {
    this.deleteClicked.emit(this.result());
  }

  isSyncInProgress(): boolean {
    const resultValue = this.result();
    if (resultValue.hasOwnProperty('isSyncInProgress')) {
      const result = resultValue as UiEndUserResult;
      return result.isSyncInProgress;
    }

    return false;
  }

  private _init(siteSummary: SiteSummary): void {
    const ciscoResult = this.result() as
      | DirectoryNumberResult
      | DistributionListResult
      | UiEndUserResult
      | PhoneResult
      | SnrProfileResult
      | VoicemailResult
      | ExtensionMobilityResult;

    if (ciscoResult.ref?.url.includes('voicemails') || ciscoResult.ref?.url.includes('extension-mobility-profiles')) {
      // VMs don't have a devicepool like phones/snrs so we must look at every site that is on the same unity server
      const sites: SiteResult[] = [];
      siteSummary.clusters.forEach((cluster: ClusterResult) => {
        if (ciscoResult.ref?.url.includes('extension-mobility-profiles')) {
          if (cluster.cucmServerId === ciscoResult.ref.serverId) {
            cluster.sites.forEach((site) => {
              sites.push(site);
            });
          }
        } else {
          cluster.sites.forEach((siteResult: SiteResult) => {
            if (siteResult.unityServerId === ciscoResult.ref.serverId) {
              sites.push(siteResult);
            }
          });
        }
      });

      const hasPermission = sites.some((siteResult: SiteResult) => siteResult.hasPermission);

      if (!hasPermission) {
        if (ciscoResult.ref?.url.includes('voicemails')) {
          this.pinReset = {
            ...this.pinReset,
            tooltip: 'tkey;search.devices.no_permissions_tooltip',
            isDisabled: true,
          };
        }

        this.delete = {
          isDeletable: true,
          isDisabled: true,
          tooltip: 'tkey;search.devices.no_permissions_tooltip',
        };
      }
    } else if (ciscoResult.ref?.url.includes('phones') || ciscoResult.ref?.url.includes('snr-profiles')) {
      const result = this.result() as PhoneResult | SnrProfileResult;
      const possibleSites = this.devicePoolSites()[result.ref.serverId];
      const siteResult = possibleSites?.find((possibleSite) => {
        return possibleSite.devicePools.some((devicePool) => devicePool.devicePool === result.ref.devicePool);
      });

      if (!siteResult) {
        this.edit = {
          ...this.edit,
          tooltip: 'tkey;search.devices.unconfigured_device_pool_tooltip',
          isDisabled: true,
        };
      } else if (!siteResult.hasPermission) {
        this.edit = {
          ...this.edit,
          tooltip: 'tkey;search.devices.no_permissions_tooltip',
          isDisabled: true,
        };

        this.delete = {
          isDeletable: true,
          isDisabled: true,
          tooltip: 'tkey;search.devices.no_permissions_tooltip',
        };
      }
    }
  }
}
